<ng-container rtBlock="sc-blog-article-page">
    @let current = article();

    @if (current !== null) {
        <article>
            <sc-page-header>
                <h1 slot="title" rtElem="title">{{ current.title }}</h1>
                <p slot="lead" rtElem="lead">{{ current.description }}</p>
            </sc-page-header>

            <div rtElem="body">
                <aside rtElem="toc" aria-label="Содержание" i18n-aria-label="@@blogTocLabel">
                    <h2 rtElem="toc-title" i18n="@@blogTocHead">Содержание</h2>
                    <ol rtElem="toc-list">
                        @for (entry of contents(); track entry.blockId) {
                            <li rtElem="toc-item">
                                <a
                                    rtElem="toc-link"
                                    [attr.href]="'#' + entry.blockId"
                                    [rtMod]="{ active: activeSectionId() === entry.blockId }"
                                    (click)="onTocClick($event, entry.blockId)">
                                    {{ entry.title }}
                                </a>
                            </li>
                        }
                    </ol>
                </aside>

                <main rtElem="main">
                    @if (current.imageSrc; as src) {
                        <figure rtElem="cover">
                            <img
                                rtElem="cover-img"
                                decoding="async"
                                sizes="(width <= 860px) 100vw, 820px"
                                [src]="src"
                                [attr.srcset]="current.imageSrcset"
                                [alt]="current.title" />
                        </figure>
                    }

                    @for (view of blocks(); track view.block.id) {
                        @switch (view.block.type) {
                            @case (BlockType.Heading2) {
                                <h2 rtElem="section-h2" [id]="view.headingId">
                                    <span rtElem="section-num">{{ view.number }} /</span>
                                    <span [innerHTML]="view.html"></span>
                                </h2>
                            }
                            @case (BlockType.Heading3) {
                                <h3 rtElem="section-h3" [innerHTML]="view.html"></h3>
                            }
                            @case (BlockType.Paragraph) {
                                <p rtElem="section-p" [innerHTML]="view.html"></p>
                            }
                            @case (BlockType.NumberedList) {
                                <ol rtElem="section-list">
                                    @for (item of view.items; track $index) {
                                        <li [innerHTML]="item"></li>
                                    }
                                </ol>
                            }
                            @case (BlockType.BulletedList) {
                                <ul rtElem="section-list">
                                    @for (item of view.items; track $index) {
                                        <li [innerHTML]="item"></li>
                                    }
                                </ul>
                            }
                            @case (BlockType.List) {
                                <ul rtElem="section-list">
                                    @for (item of view.items; track $index) {
                                        <li [innerHTML]="item"></li>
                                    }
                                </ul>
                            }
                            @case (BlockType.Note) {
                                @if (view.note; as note) {
                                    <div rtElem="callout">
                                        @if (note.title) {
                                            <div rtElem="callout-tag">{{ note.title }}</div>
                                        }
                                        <div [innerHTML]="note.text"></div>
                                    </div>
                                }
                            }
                            @case (BlockType.Image) {
                                <sc-blog-image-block qa-dataid="blog-image-block" [content]="view.block.content" />
                            }
                            @case (BlockType.Quote) {
                                <sc-blog-quote-block [content]="view.block.content" />
                            }
                            @case (BlockType.EmbeddedLink) {
                                <sc-blog-embed-block qa-dataid="blog-embed-block" [content]="view.block.content" />
                            }
                            @case (BlockType.Button) {
                                <sc-blog-button-block qa-dataid="blog-button-block" [content]="view.block.content" />
                            }
                            @case (BlockType.ProsCons) {
                                <sc-blog-pros-cons-block [content]="view.block.content" />
                            }
                            @case (BlockType.Accordion) {
                                <sc-blog-accordion-block [content]="view.block.content" />
                            }
                            @case (BlockType.ContentItemLink) {
                                @if (view.linked; as linked) {
                                    <sc-blog-article-card
                                        groupLabel=""
                                        [slug]="linked.slug"
                                        [path]="linked.path"
                                        [title]="linked.title"
                                        [description]="linked.description"
                                        [imageSrc]="linked.imageSrc"
                                        [imageSrcset]="linked.imageSrcset" />
                                }
                            }
                        }
                    }
                </main>
            </div>
        </article>

        @if (relatedArticles().length > 0) {
            <section rtElem="related" aria-labelledby="related-title">
                <h2 rtElem="related-title" id="related-title" i18n="@@blogRelatedTitle">Похожие статьи</h2>
                <div rtElem="related-grid">
                    @for (related of relatedArticles(); track related.article.id) {
                        <sc-blog-article-card
                            [slug]="related.article.slug"
                            [path]="related.article.path"
                            [groupLabel]="related.rubric"
                            [title]="related.article.title"
                            [description]="related.article.description"
                            [imageSrc]="related.article.imageSrc"
                            [imageSrcset]="related.article.imageSrcset" />
                    }
                </div>
            </section>
        }
    } @else if (loading()) {
        <div rtElem="skeleton" qa-dataid="blog-article-skeleton" aria-busy="true">
            <sc-page-header qa-skip>
                <rt-skeleton-wrapper qa-skip slot="title" isLoading width="55%" height="40px" />
                <rt-skeleton-wrapper qa-skip slot="lead" isLoading width="420px" height="16px" />
            </sc-page-header>

            <div rtElem="body">
                <div rtElem="toc">
                    <div rtElem="skeleton-lines">
                        <rt-skeleton-wrapper qa-skip isLoading width="40%" height="12px" />
                        <rt-skeleton-wrapper qa-skip isLoading width="90%" height="14px" />
                        <rt-skeleton-wrapper qa-skip isLoading width="80%" height="14px" />
                        <rt-skeleton-wrapper qa-skip isLoading width="85%" height="14px" />
                    </div>
                </div>

                <div rtElem="main">
                    <div rtElem="skeleton-cover">
                        <rt-skeleton-wrapper qa-skip isLoading width="100%" height="100%" />
                    </div>
                    <div rtElem="skeleton-lines">
                        <rt-skeleton-wrapper qa-skip isLoading width="50%" height="24px" />
                        <rt-skeleton-wrapper qa-skip isLoading width="100%" height="14px" />
                        <rt-skeleton-wrapper qa-skip isLoading width="100%" height="14px" />
                        <rt-skeleton-wrapper qa-skip isLoading width="92%" height="14px" />
                        <rt-skeleton-wrapper qa-skip isLoading width="70%" height="14px" />
                    </div>
                </div>
            </div>
        </div>
    }
</ng-container>
